import { Controller } from "@hotwired/stimulus"
import { adjacentAnchor, matchingAnchor } from "../lib/settings_tabs.mjs"
import { revealActiveItem } from "../lib/scroll_strip.mjs"

/**
 * Settings Tabs Controller
 *
 * Provides tabbed navigation for the unified settings page.
 * - Exactly one settings section is visible at a time
 * - The selected section is represented by the URL hash for direct linking
 * - Native links keep settings usable when JavaScript is unavailable
 */
export default class extends Controller {
  static targets = ["tab", "section", "strip"]

  connect() {
    this.element.dataset.settingsTabsReady = "true"
    this.handleHashChange()

    this.hashHandler = this.handleHashChange.bind(this)
    window.addEventListener("hashchange", this.hashHandler)
  }

  disconnect() {
    window.removeEventListener("hashchange", this.hashHandler)
  }

  navigate(event) {
    const anchor = event.currentTarget.dataset.anchor
    if (!this.anchors().includes(anchor)) return

    event.preventDefault()
    history.pushState(null, null, `#${anchor}`)
    this.select(anchor)
  }

  handleHashChange() {
    this.select(matchingAnchor(this.anchors(), window.location.hash))
  }

  move(event) {
    const anchors = this.anchors()
    const current = event.target.closest('[role="tab"]')?.dataset.anchor
    if (!current) return
    let next
    if (event.key === "ArrowRight") next = adjacentAnchor(anchors, current, 1)
    if (event.key === "ArrowLeft") next = adjacentAnchor(anchors, current, -1)
    if (event.key === "Home") next = anchors[0]
    if (event.key === "End") next = anchors.at(-1)
    if (!next) return

    event.preventDefault()
    history.pushState(null, null, `#${next}`)
    this.select(next)
    this.tabTargets.find(tab => tab.dataset.anchor === next)?.focus()
  }

  anchors() {
    return this.tabTargets.map(tab => tab.dataset.anchor)
  }

  select(anchor) {
    if (!anchor) return

    let activeTab
    this.tabTargets.forEach(tab => {
      const active = tab.dataset.anchor === anchor
      if (active) activeTab = tab
      if (active) {
        tab.dataset.active = ""
      } else {
        delete tab.dataset.active
      }
      tab.setAttribute("aria-selected", String(active))
      tab.tabIndex = active ? 0 : -1
    })

    this.sectionTargets.forEach(section => {
      const active = section.id === anchor
      section.hidden = !active
      section.setAttribute("aria-hidden", String(!active))
    })

    if (this.hasStripTarget && activeTab) {
      requestAnimationFrame(() => revealActiveItem(this.stripTarget, activeTab))
    }
  }
}
